import React, { createContext, useContext } from 'react';

type AccordionHeaderToggleProps = {
  contentId: string;
  isDisabled: boolean;
  isExpanded: boolean;
};

/** Id del panel del item de AccordionList2 en cuya cabecera se pinta. */
export const AccordionItemContentIdContext = createContext<string | undefined>(undefined);

export function getAccordionItemLabelId(contentId: string): string {
  return `${contentId}-label`;
}

/** Id que debe llevar el título de un item con `hasInnerToggle`: da nombre a su conmutador. */
export function useAccordionItemLabelId(): string | undefined {
  const contentId = useContext(AccordionItemContentIdContext);
  return contentId ? getAccordionItemLabelId(contentId) : undefined;
}

/**
 * AccordionHeaderToggle - Conmutador de un item de AccordionList2 con `hasInnerToggle`, es decir,
 * cuya cabecera contiene otros controles («Más opciones», «Ver kanban»…).
 *
 * `AccordionSummary` es un `role="button"`: cualquier control dentro de él es `nested-interactive`
 * (WCAG 4.1.2). En su lugar, la cabecera queda sin rol ni parada de Tab y este botón vacío la cubre
 * (patrón overlay, TAN-9855), con el título como nombre (`aria-labelledby`). No recibe el puntero:
 * los clics siguen llegando a la cabecera y a sus controles, y Enter/Espacio generan su clic nativo.
 */
function AccordionHeaderToggle({ contentId, isDisabled, isExpanded }: AccordionHeaderToggleProps): JSX.Element {
  return (
    <button
      type="button"
      aria-controls={contentId}
      aria-expanded={isExpanded}
      aria-labelledby={getAccordionItemLabelId(contentId)}
      className="accordion-list2__toggle"
      disabled={isDisabled}
    />
  );
}

export default AccordionHeaderToggle;
